import { useEffect, useState, useRef, useCallback } from "react";
import {
  MdEdit,
  MdClose,
  MdUpload,
  MdAdd,
  MdDelete,
  MdDragIndicator,
} from "react-icons/md";
import Modal from "react-modal";
import { toast } from "react-toastify";
import { adminApiService } from "services/api";

Modal.setAppElement("#root");

const LoadingSpinner = () => (
  <div className="flex items-center justify-center">
    <div className="border-t-transparent h-5 w-5 animate-spin rounded-full border-2 border-blue-500 border-opacity-50"></div>
  </div>
);

const VideoPreview = ({ title, videoUrl, onEdit, onDelete }) => (
  <div className="relative overflow-hidden rounded-lg border border-gray-200 shadow-sm">
    <video
      src={videoUrl}
      controls={false}
      className="h-48 w-full object-cover"
      muted
      loop
      autoPlay
      playsInline
    />
    <div className="bg-black absolute inset-0 flex items-center justify-center bg-opacity-0 opacity-0 transition-all hover:bg-opacity-30 hover:opacity-100">
      <div className="flex space-x-2">
        <button
          onClick={onEdit}
          className="rounded-full bg-white p-2 text-blue-600 shadow-md hover:bg-blue-50"
        >
          <MdEdit size={16} />
        </button>
        <button
          onClick={onDelete}
          className="rounded-full bg-white p-2 text-red-600 shadow-md hover:bg-red-50"
        >
          <MdDelete size={16} />
        </button>
      </div>
    </div>
    <div className="p-3">
      <p className="font-medium text-gray-800">{title}</p>
    </div>
  </div>
);

const ImageSlideItem = ({
  slideData,
  index,
  onDelete,
  onMoveUp,
  onMoveDown,
  onEdit,
  totalCount,
}) => {
  // Handle both old format (string) and new format (object)
  const imageName = typeof slideData === "string" ? slideData : slideData.image;
  const slideTitle =
    typeof slideData === "object" ? slideData.title : `Slide ${index + 1}`;
  const slideText = typeof slideData === "object" ? slideData.text : "";

  return (
    <div className="group relative overflow-hidden rounded-lg border border-gray-200 shadow-sm">
      <img
        src={`${process.env.REACT_APP_API_URL_Plain}/config/uploads/hero_media/slides/${imageName}`}
        alt={`Slide ${index + 1}`}
        className="h-32 w-full object-cover"
      />

      {/* Title and text overlay - only show if slide has text content */}
      {(slideTitle || slideText) && (
        <div className="from-black/80 to-transparent absolute bottom-0 left-0 right-0 bg-gradient-to-t p-2">
          <div className="text-white">
            {slideTitle && (
              <p className="truncate text-xs font-medium">{slideTitle}</p>
            )}
            {slideText && (
              <p className="truncate text-xs opacity-80">{slideText}</p>
            )}
          </div>
        </div>
      )}

      {/* Hover controls */}
      <div className="bg-black absolute inset-0 flex items-center justify-center bg-opacity-0 opacity-0 transition-all group-hover:bg-opacity-30 group-hover:opacity-100">
        <div className="flex space-x-1">
          <button
            onClick={() => onEdit(index)}
            className="rounded bg-white p-1 text-xs text-green-600 shadow-md hover:bg-green-50"
            title="Edit content"
          >
            <MdEdit size={12} />
          </button>
          {index > 0 && (
            <button
              onClick={() => onMoveUp(index)}
              className="rounded bg-white p-1 text-xs text-blue-600 shadow-md hover:bg-blue-50"
              title="Move up"
            >
              ↑
            </button>
          )}
          {index < totalCount - 1 && (
            <button
              onClick={() => onMoveDown(index)}
              className="rounded bg-white p-1 text-xs text-blue-600 shadow-md hover:bg-blue-50"
              title="Move down"
            >
              ↓
            </button>
          )}
          <button
            onClick={() => onDelete(index)}
            className="rounded bg-white p-1 text-red-600 shadow-md hover:bg-red-50"
            title="Delete image"
          >
            <MdDelete size={12} />
          </button>
        </div>
      </div>

      {/* Position indicator */}
      <div className="bg-black absolute left-1 top-1 rounded bg-opacity-70 px-1 py-0.5 text-xs text-white">
        {index + 1}
      </div>
    </div>
  );
};

const FileUploadArea = ({
  onFileChange,
  accept = "*",
  multiple = false,
  label,
  previewUrl,
  previewUrls,
}) => (
  <div className="mt-2">
    <label className="flex w-full cursor-pointer flex-col items-center justify-center rounded-lg border-2 border-dashed border-gray-300 bg-gray-50 px-6 py-8 hover:bg-gray-100">
      {previewUrl ? (
        <video
          src={previewUrl}
          controls={false}
          className="mb-2 h-32 w-full object-cover"
          muted
        />
      ) : previewUrls && previewUrls.length > 0 ? (
        <div className="grid w-full grid-cols-3 gap-2">
          {previewUrls.slice(0, 6).map((url, index) => (
            <img
              key={index}
              src={url}
              alt={`Preview ${index + 1}`}
              className="h-16 w-full rounded object-cover"
            />
          ))}
          {previewUrls.length > 6 && (
            <div className="flex h-16 w-full items-center justify-center rounded bg-gray-200">
              <span className="text-xs text-gray-600">
                +{previewUrls.length - 6}
              </span>
            </div>
          )}
        </div>
      ) : (
        <div className="flex flex-col items-center justify-center text-center">
          <MdUpload className="mx-auto h-8 w-8 text-gray-400" />
          <p className="mt-1 text-sm text-gray-600">
            <span className="font-medium text-blue-600">Click to upload</span>{" "}
            or drag and drop
          </p>
          <p className="text-xs text-gray-500">{label}</p>
        </div>
      )}
      <input
        type="file"
        accept={accept}
        onChange={onFileChange}
        className="hidden"
        multiple={multiple}
      />
    </label>
  </div>
);

export default function HeroMedia() {
  const [mediaList, setMediaList] = useState([]);
  const [currentMode, setCurrentMode] = useState("video"); // "video" or "images"
  const [isLoading, setIsLoading] = useState(false);
  const [modalState, setModalState] = useState({
    isOpen: false,
    type: "", // "edit-video", "add-video", "add-images", "edit-slide"
    data: null,
  });

  const [slideContentForm, setSlideContentForm] = useState({
    title: "",
    text: "",
    index: -1,
  });
  const [uploadFiles, setUploadFiles] = useState({
    desktop: null,
    mobile: null,
    images: [],
  });

  const [imageTextData, setImageTextData] = useState([]);
  const [previewUrls, setPreviewUrls] = useState({
    desktop: "",
    mobile: "",
    images: [],
  });

  const fetchMedia = useCallback(async () => {
    setIsLoading(true);
    try {
      const res = await adminApiService.getHeroMedia();
      if (!res.ok) throw new Error("Failed to fetch media");
      const { data } = await res.json();
      setMediaList(data);

      // Set default mode based on existing data
      if (data.length > 0) {
        setCurrentMode(data[0].is_video ? "video" : "images");
      }
    } catch (error) {
      toast.error("Error fetching media.");
      console.error("Fetch error:", error);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchMedia();
  }, [fetchMedia]);

  const openModal = (type, data = null) => {
    setModalState({ isOpen: true, type, data });
    setUploadFiles({ desktop: null, mobile: null, images: [] });
    setPreviewUrls({ desktop: "", mobile: "", images: [] });
    setImageTextData([]);
    setSlideContentForm({ title: "", text: "", enableText: false, index: -1 });
  };

  const closeModal = () => {
    setModalState({ isOpen: false, type: "", data: null });
    setUploadFiles({ desktop: null, mobile: null, images: [] });
    setPreviewUrls({ desktop: "", mobile: "", images: [] });
    setImageTextData([]);
    setSlideContentForm({ title: "", text: "", enableText: false, index: -1 });
  };

  const openSlideEditModal = (index) => {
    const slide = currentMedia.slide_images[index];
    const slideTitle = typeof slide === "object" ? slide.title : "";
    const slideText = typeof slide === "object" ? slide.text : "";
    const enableText =
      typeof slide === "object"
        ? slide.title || slide.text
          ? true
          : false
        : false;

    setSlideContentForm({
      title: slideTitle,
      text: slideText,
      enableText: enableText,
      index: index,
    });
    setModalState({ isOpen: true, type: "edit-slide", data: slide });
  };

  const handleFileChange = (field, e) => {
    const files = e.target.files;
    if (!files) return;

    if (field === "images") {
      const filesArray = Array.from(files);
      setUploadFiles((prev) => ({ ...prev, images: filesArray }));
      const previewUrls = filesArray.map((file) => URL.createObjectURL(file));
      setPreviewUrls((prev) => ({ ...prev, images: previewUrls }));

      // Initialize text data for new images
      const newImageTextData = filesArray.map((_, index) => ({
        enableText: false,
        title: "",
        text: "",
      }));
      setImageTextData(newImageTextData);
    } else {
      const file = files[0];
      setUploadFiles((prev) => ({ ...prev, [field]: file }));
      setPreviewUrls((prev) => ({
        ...prev,
        [field]: URL.createObjectURL(file),
      }));
    }
  };

  const handleModeChange = async (newMode) => {
    if (mediaList.length === 0) {
      setCurrentMode(newMode);
      return;
    }

    const isVideoMode = newMode === "video";
    setIsLoading(true);

    try {
      const formData = new FormData();
      formData.append("id", mediaList[0].id);
      formData.append("is_video", isVideoMode);

      const res = await adminApiService.updateHeroMedia(formData);

      if (!res.ok) throw new Error("Failed to update media type");

      setCurrentMode(newMode);
      toast.success(
        `Switched to ${newMode === "video" ? "video" : "image slides"} mode!`
      );
      fetchMedia();
    } catch (error) {
      toast.error("Error switching mode.");
      console.error("Mode change error:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleVideoSubmit = async (e) => {
    e.preventDefault();

    const isEdit = modalState.type === "edit-video";
    const { desktop, mobile } = uploadFiles;

    if (!isEdit && (!desktop || !mobile)) {
      return toast.error("Please upload both desktop and mobile videos.");
    }

    setIsLoading(true);
    try {
      const formData = new FormData();
      formData.append("is_video", true);

      if (isEdit) {
        formData.append("id", modalState.data.id);
        if (desktop) formData.append("hero_media", desktop);
        if (mobile) formData.append("mobile_hero_media", mobile);
      } else {
        formData.append("hero_media", desktop);
        formData.append("mobile_hero_media", mobile);
      }

      const res = isEdit
        ? await adminApiService.updateHeroMedia(formData)
        : await adminApiService.addHeroMedia(formData);

      if (!res.ok)
        throw new Error(`Failed to ${isEdit ? "update" : "add"} video`);

      toast.success(`Video ${isEdit ? "updated" : "added"} successfully!`);
      closeModal();
      fetchMedia();
    } catch (error) {
      toast.error(
        `Error ${
          modalState.type === "edit-video" ? "updating" : "adding"
        } video.`
      );
      console.error("Video submit error:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleImagesSubmit = async (e) => {
    e.preventDefault();

    if (uploadFiles.images.length === 0) {
      return toast.error("Please upload at least one image.");
    }

    setIsLoading(true);
    try {
      const formData = new FormData();
      formData.append("is_video", false);

      uploadFiles.images.forEach((file) => {
        formData.append("slide_images", file);
      });

      // Include title and text data
      formData.append("slide_data", JSON.stringify(imageTextData));

      const res = await fetch(
        `${process.env.REACT_APP_API_URL}/cms/add-hero-media`,
        {
          method: "POST",
          body: formData,
        }
      );

      if (!res.ok) throw new Error("Failed to add images");

      const hasExisting = currentMedia && currentMedia.slide_images?.length > 0;
      toast.success(
        hasExisting
          ? `${uploadFiles.images.length} images added successfully!`
          : "Images added successfully!"
      );
      closeModal();
      fetchMedia();
    } catch (error) {
      toast.error("Error adding images.");
      console.error("Images submit error:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleImageReorder = async (fromIndex, toIndex) => {
    const currentImages = [...mediaList[0].slide_images];
    const [movedImage] = currentImages.splice(fromIndex, 1);
    currentImages.splice(toIndex, 0, movedImage);

    setIsLoading(true);
    try {
      const formData = new FormData();
      formData.append("id", mediaList[0].id);
      formData.append("slide_order", JSON.stringify(currentImages));

      const res = await adminApiService.updateHeroMedia(formData);

      if (!res.ok) throw new Error("Failed to reorder images");

      toast.success("Images reordered successfully!");
      fetchMedia();
    } catch (error) {
      toast.error("Error reordering images.");
      console.error("Reorder error:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSlideContentUpdate = async (e) => {
    e.preventDefault();

    // Only validate title if text overlay is enabled and title is provided
    if (
      slideContentForm.enableText &&
      !slideContentForm.title.trim() &&
      !slideContentForm.text.trim()
    ) {
      return toast.error(
        "Please enter either a title or description when text overlay is enabled."
      );
    }

    setIsLoading(true);
    try {
      const res = await fetch(
        `${process.env.REACT_APP_API_URL}/cms/update-slide-content/${mediaList[0].id}`,
        {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            slideIndex: slideContentForm.index,
            title: slideContentForm.enableText ? slideContentForm.title : "",
            text: slideContentForm.enableText ? slideContentForm.text : "",
            enableText: slideContentForm.enableText,
          }),
        }
      );

      if (!res.ok) throw new Error("Failed to update slide content");

      toast.success("Slide content updated successfully!");
      closeModal();
      fetchMedia();
    } catch (error) {
      toast.error("Error updating slide content.");
      console.error("Slide content update error:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleImageDelete = async (imageIndex) => {
    if (!window.confirm("Are you sure you want to delete this image?")) return;

    const currentImages = [...mediaList[0].slide_images];
    currentImages.splice(imageIndex, 1);

    setIsLoading(true);
    try {
      const formData = new FormData();
      formData.append("id", mediaList[0].id);
      formData.append("slide_order", JSON.stringify(currentImages));

      const res = await fetch(
        `${process.env.REACT_APP_API_URL}/cms/update-hero-media`,
        {
          method: "PUT",
          body: formData,
        }
      );

      if (!res.ok) throw new Error("Failed to delete image");

      toast.success("Image deleted successfully!");
      fetchMedia();
    } catch (error) {
      toast.error("Error deleting image.");
      console.error("Delete error:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const currentMedia = mediaList.length > 0 ? mediaList[0] : null;

  return (
    <div className="mx-auto max-w-7xl p-6">
      <header className="mb-8">
        <h1 className="text-3xl font-bold text-gray-800">
          Hero Media Management
        </h1>
        <p className="mt-2 text-gray-600">
          Manage the media displayed in the hero section
        </p>
      </header>

      {/* Mode Selection */}
      <div className="mb-8 rounded-lg bg-white p-6 shadow-sm">
        <h2 className="mb-4 text-lg font-semibold text-gray-800">Media Type</h2>
        <div className="flex space-x-6">
          <label className="flex items-center">
            <input
              type="radio"
              name="mediaMode"
              value="video"
              checked={currentMode === "video"}
              onChange={() => handleModeChange("video")}
              disabled={isLoading}
              className="mr-2"
            />
            <span className="text-sm font-medium text-gray-700">
              Video Mode
            </span>
          </label>
          <label className="flex items-center">
            <input
              type="radio"
              name="mediaMode"
              value="images"
              checked={currentMode === "images"}
              onChange={() => handleModeChange("images")}
              disabled={isLoading}
              className="mr-2"
            />
            <span className="text-sm font-medium text-gray-700">
              Image Slide Mode
            </span>
          </label>
        </div>
      </div>

      {isLoading && !currentMedia ? (
        <div className="flex h-64 items-center justify-center">
          <LoadingSpinner />
        </div>
      ) : (
        <div className="space-y-8">
          {currentMode === "video" ? (
            // Video Mode
            <div className="rounded-lg bg-white p-6 shadow-sm">
              <div className="mb-4 flex items-center justify-between">
                <h2 className="text-lg font-semibold text-gray-800">
                  Hero Videos
                </h2>
                <button
                  onClick={() =>
                    openModal(
                      currentMedia ? "edit-video" : "add-video",
                      currentMedia
                    )
                  }
                  className="flex items-center gap-2 rounded-lg bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700"
                >
                  {currentMedia ? <MdEdit size={16} /> : <MdAdd size={16} />}
                  {currentMedia ? "Edit Videos" : "Add Videos"}
                </button>
              </div>

              {currentMedia && currentMedia.is_video ? (
                <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
                  <VideoPreview
                    title="Desktop Video"
                    videoUrl={`${process.env.REACT_APP_API_URL_Plain}/config/uploads/hero_media/${currentMedia.media_name}`}
                    onEdit={() => openModal("edit-video", currentMedia)}
                  />
                  <VideoPreview
                    title="Mobile Video"
                    videoUrl={`${process.env.REACT_APP_API_URL_Plain}/config/uploads/hero_media/mobile_media/${currentMedia.mobile_media_name}`}
                    onEdit={() => openModal("edit-video", currentMedia)}
                  />
                </div>
              ) : (
                <div className="flex h-32 items-center justify-center rounded-lg border-2 border-dashed border-gray-300 bg-gray-50">
                  <p className="text-gray-500">No videos uploaded yet</p>
                </div>
              )}
            </div>
          ) : (
            // Image Slide Mode
            <div className="rounded-lg bg-white p-6 shadow-sm">
              <div className="mb-4 flex items-center justify-between">
                <h2 className="text-lg font-semibold text-gray-800">
                  Hero Image Slides
                  {currentMedia && currentMedia.slide_images?.length > 0 && (
                    <span className="ml-2 text-sm font-normal text-gray-500">
                      ({currentMedia.slide_images.length} images)
                    </span>
                  )}
                </h2>
                <button
                  onClick={() => openModal("add-images")}
                  className="flex items-center gap-2 rounded-lg bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700"
                >
                  <MdAdd size={16} />
                  {currentMedia && currentMedia.slide_images?.length > 0
                    ? "Add More Images"
                    : "Add Images"}
                </button>
              </div>

              {currentMedia &&
              !currentMedia.is_video &&
              currentMedia.slide_images?.length > 0 ? (
                <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6">
                  {currentMedia.slide_images.map((slideData, index) => (
                    <ImageSlideItem
                      key={index}
                      slideData={slideData}
                      index={index}
                      onDelete={handleImageDelete}
                      onMoveUp={(idx) => handleImageReorder(idx, idx - 1)}
                      onMoveDown={(idx) => handleImageReorder(idx, idx + 1)}
                      onEdit={openSlideEditModal}
                      totalCount={currentMedia.slide_images.length}
                    />
                  ))}
                </div>
              ) : (
                <div className="flex h-32 items-center justify-center rounded-lg border-2 border-dashed border-gray-300 bg-gray-50">
                  <p className="text-gray-500">No images uploaded yet</p>
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* Modal */}
      <Modal
        isOpen={modalState.isOpen}
        onRequestClose={closeModal}
        contentLabel="Media Modal"
        className="relative mx-auto max-w-lg rounded-xl bg-white p-6 shadow-2xl outline-none"
        overlayClassName="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50 backdrop-blur-sm"
        closeTimeoutMS={200}
      >
        <button
          onClick={closeModal}
          className="absolute right-4 top-4 rounded-full p-1 text-gray-500 hover:bg-gray-100"
        >
          <MdClose size={24} />
        </button>

        <h2 className="mb-6 text-2xl font-semibold text-gray-800">
          {modalState.type === "edit-video" && "Edit Videos"}
          {modalState.type === "add-video" && "Add New Videos"}
          {modalState.type === "add-images" && "Add New Images"}
          {modalState.type === "edit-slide" && "Edit Slide Content"}
        </h2>

        {(modalState.type === "edit-video" ||
          modalState.type === "add-video") && (
          <form onSubmit={handleVideoSubmit} className="space-y-6">
            <div>
              <label className="mb-2 block text-sm font-medium text-gray-700">
                Desktop Video{" "}
                {modalState.type === "edit-video" ? "(optional)" : "(required)"}
              </label>
              <FileUploadArea
                onFileChange={(e) => handleFileChange("desktop", e)}
                accept="video/*"
                label="MP4, MOV, AVI up to 50MB"
                previewUrl={previewUrls.desktop}
              />
            </div>

            <div>
              <label className="mb-2 block text-sm font-medium text-gray-700">
                Mobile Video{" "}
                {modalState.type === "edit-video" ? "(optional)" : "(required)"}
              </label>
              <FileUploadArea
                onFileChange={(e) => handleFileChange("mobile", e)}
                accept="video/*"
                label="MP4, MOV, AVI up to 50MB"
                previewUrl={previewUrls.mobile}
              />
            </div>

            <div className="flex justify-end space-x-3">
              <button
                type="button"
                onClick={closeModal}
                className="rounded-lg border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
                disabled={isLoading}
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={isLoading}
                className="flex items-center justify-center rounded-lg bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700 disabled:opacity-70"
              >
                {isLoading ? (
                  <>
                    <LoadingSpinner />{" "}
                    {modalState.type === "edit-video"
                      ? "Updating..."
                      : "Adding..."}
                  </>
                ) : modalState.type === "edit-video" ? (
                  "Update Videos"
                ) : (
                  "Add Videos"
                )}
              </button>
            </div>
          </form>
        )}

        {modalState.type === "add-images" && (
          <form onSubmit={handleImagesSubmit} className="space-y-6">
            <div>
              <label className="mb-2 block text-sm font-medium text-gray-700">
                Upload Images (required)
              </label>
              <FileUploadArea
                onFileChange={(e) => handleFileChange("images", e)}
                accept="image/*"
                multiple={true}
                label="JPG, PNG, GIF up to 10MB each"
                previewUrls={previewUrls.images}
              />
            </div>

            {/* Title and Text inputs for each image */}
            {uploadFiles.images.length > 0 && (
              <div className="space-y-4">
                <h3 className="text-sm font-medium text-gray-700">
                  Configure each image slide:
                </h3>
                {uploadFiles.images.map((file, index) => (
                  <div
                    key={index}
                    className="rounded-lg border border-gray-200 p-4"
                  >
                    <div className="mb-3 flex items-center space-x-3">
                      <img
                        src={previewUrls.images[index]}
                        alt={`Preview ${index + 1}`}
                        className="h-12 w-12 rounded object-cover"
                      />
                      <span className="text-sm font-medium text-gray-600">
                        Image {index + 1}: {file.name}
                      </span>
                    </div>

                    {/* Toggle for enabling text overlay */}
                    <div className="mb-3">
                      <label className="flex items-center space-x-2">
                        <input
                          type="checkbox"
                          checked={imageTextData[index]?.enableText || false}
                          onChange={(e) => {
                            const newData = [...imageTextData];
                            newData[index] = {
                              ...newData[index],
                              enableText: e.target.checked,
                              // Clear title and text if disabling
                              title: e.target.checked
                                ? newData[index]?.title || ""
                                : "",
                              text: e.target.checked
                                ? newData[index]?.text || ""
                                : "",
                            };
                            setImageTextData(newData);
                          }}
                          className="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                        />
                        <span className="text-sm font-medium text-gray-700">
                          Enable text overlay for this slide
                        </span>
                      </label>
                    </div>

                    {/* Title and Description fields - only show when text overlay is enabled */}
                    {imageTextData[index]?.enableText && (
                      <div className="space-y-3 border-l-2 border-blue-200 pl-6">
                        <div>
                          <label className="mb-1 block text-xs font-medium text-gray-600">
                            Title (optional)
                          </label>
                          <input
                            type="text"
                            value={imageTextData[index]?.title || ""}
                            onChange={(e) => {
                              const newData = [...imageTextData];
                              newData[index] = {
                                ...newData[index],
                                title: e.target.value,
                              };
                              setImageTextData(newData);
                            }}
                            placeholder={`Title for slide ${index + 1}`}
                            className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none"
                          />
                        </div>
                        <div>
                          <label className="mb-1 block text-xs font-medium text-gray-600">
                            Description (optional)
                          </label>
                          <textarea
                            value={imageTextData[index]?.text || ""}
                            onChange={(e) => {
                              const newData = [...imageTextData];
                              newData[index] = {
                                ...newData[index],
                                text: e.target.value,
                              };
                              setImageTextData(newData);
                            }}
                            placeholder={`Description for slide ${index + 1}`}
                            rows={3}
                            className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none"
                          />
                        </div>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}

            <div className="flex justify-end space-x-3">
              <button
                type="button"
                onClick={closeModal}
                className="rounded-lg border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
                disabled={isLoading}
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={isLoading}
                className="flex items-center justify-center rounded-lg bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700 disabled:opacity-70"
              >
                {isLoading ? (
                  <>
                    <LoadingSpinner /> Adding...
                  </>
                ) : (
                  "Add Images"
                )}
              </button>
            </div>
          </form>
        )}

        {modalState.type === "edit-slide" && (
          <form onSubmit={handleSlideContentUpdate} className="space-y-6">
            <div className="mb-4">
              <div className="flex items-center space-x-3">
                <img
                  src={`${
                    process.env.REACT_APP_API_URL_Plain
                  }/config/uploads/hero_media/slides/${
                    typeof modalState.data === "string"
                      ? modalState.data
                      : modalState.data.image
                  }`}
                  alt="Slide preview"
                  className="h-16 w-16 rounded object-cover"
                />
                <div>
                  <p className="text-sm font-medium text-gray-700">
                    Editing Slide {slideContentForm.index + 1}
                  </p>
                  <p className="text-xs text-gray-500">
                    File:{" "}
                    {typeof modalState.data === "string"
                      ? modalState.data
                      : modalState.data.image}
                  </p>
                </div>
              </div>
            </div>

            {/* Toggle for enabling text overlay */}
            <div className="mb-4">
              <label className="flex items-center space-x-2">
                <input
                  type="checkbox"
                  checked={slideContentForm.enableText || false}
                  onChange={(e) => {
                    setSlideContentForm((prev) => ({
                      ...prev,
                      enableText: e.target.checked,
                      // Clear title and text if disabling
                      title: e.target.checked ? prev.title : "",
                      text: e.target.checked ? prev.text : "",
                    }));
                  }}
                  className="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                />
                <span className="text-sm font-medium text-gray-700">
                  Enable text overlay for this slide
                </span>
              </label>
            </div>

            {/* Title and Description fields - only show when text overlay is enabled */}
            {slideContentForm.enableText && (
              <div className="space-y-4 border-l-2 border-blue-200 pl-6">
                <div>
                  <label className="mb-2 block text-sm font-medium text-gray-700">
                    Title (optional)
                  </label>
                  <input
                    type="text"
                    value={slideContentForm.title}
                    onChange={(e) =>
                      setSlideContentForm((prev) => ({
                        ...prev,
                        title: e.target.value,
                      }))
                    }
                    placeholder="Enter slide title"
                    className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none"
                  />
                </div>

                <div>
                  <label className="mb-2 block text-sm font-medium text-gray-700">
                    Description (optional)
                  </label>
                  <textarea
                    value={slideContentForm.text}
                    onChange={(e) =>
                      setSlideContentForm((prev) => ({
                        ...prev,
                        text: e.target.value,
                      }))
                    }
                    placeholder="Enter slide description"
                    rows={4}
                    className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none"
                  />
                </div>
              </div>
            )}

            <div className="flex justify-end space-x-3">
              <button
                type="button"
                onClick={closeModal}
                className="rounded-lg border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
                disabled={isLoading}
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={isLoading}
                className="flex items-center justify-center rounded-lg bg-green-600 px-4 py-2 text-sm font-medium text-white hover:bg-green-700 disabled:opacity-70"
              >
                {isLoading ? (
                  <>
                    <LoadingSpinner /> Updating...
                  </>
                ) : (
                  "Update Content"
                )}
              </button>
            </div>
          </form>
        )}
      </Modal>
    </div>
  );
}
